import { editorUrl } from '@/lib/editorBasePath';
import React, { useState, useMemo } from 'react';
import { MoreHorizontal, MessageCircle, Printer, RotateCcw, Pencil, ArrowLeftRight, Copy, ImageOff, Ruler, History } from 'lucide-react';
import AddGuideDialog from './AddGuideDialog';
import PageHistoryDialog from './PageHistoryDialog';
import { YearbookPage, YearbookElement, PageBackground, DEFAULT_PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';

interface PageActionsProps {
  page: YearbookPage;
  pageIndex: number;
  scale: number;
  pages?: YearbookPage[];
  coverName?: string;
  onRename?: (pageId: string, name: string) => void;
  onReset?: (pageIndex: number) => void;
  onSwapPages?: (indexA: number, indexB: number) => void;
  onDuplicatePageContent?: (sourceIndex: number, targetIndex: number) => void;
  onRemoveBackground?: (pageIndex: number) => void;
  onAddGuide?: (pageIndex: number, guide: { orientation: 'horizontal' | 'vertical'; position: number; applyToAll?: boolean }) => void;
  onRestoreVersion?: (pageIndex: number, elements: YearbookElement[], background?: PageBackground) => void;
}

const waitForImageLoad = (img: HTMLImageElement) => new Promise<void>((resolve) => {
  if (img.complete && img.naturalWidth > 0) {
    resolve();
    return;
  }

  const done = () => {
    img.removeEventListener('load', done);
    img.removeEventListener('error', done);
    resolve();
  };

  img.addEventListener('load', done, { once: true });
  img.addEventListener('error', done, { once: true });
  window.setTimeout(done, 5000);
});

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const collectPrintableStyles = () => Array.from(document.styleSheets).map((sheet) => {
  try {
    const css = Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
    return `<style>${css.replace(/<\/style/gi, '<\\/style')}</style>`;
  } catch {
    const href = sheet.href || ((sheet.ownerNode as HTMLLinkElement | null)?.href ?? '');
    return href ? `<link rel="stylesheet" href="${escapeHtml(href)}">` : '';
  }
}).join('\n');

const clonePageForPrint = (el: HTMLElement, pageW: number) => {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.setAttribute('data-print-page', 'true');
  clone.style.position = 'relative';
  clone.style.inset = 'auto';
  clone.style.left = '0';
  clone.style.top = '0';
  clone.style.width = `${pageW}px`;
  clone.style.height = `${PAGE_HEIGHT}px`;
  clone.style.transform = 'none';
  clone.style.transformOrigin = 'top left';
  clone.querySelectorAll('.resize-handle, .selection-overlay, [data-no-print="true"]').forEach((node) => node.remove());
  clone.querySelectorAll('.element-selected, .element-hover').forEach((node) => {
    node.classList.remove('element-selected', 'element-hover', 'hover:element-hover', 'ring-2', 'ring-blue-500', 'ring-offset-1');
  });
  clone.querySelectorAll('[contenteditable="true"]').forEach((node) => node.setAttribute('contenteditable', 'false'));
  return clone;
};

const waitForPrintAssets = async (printWindow: Window) => {
  await Promise.all(Array.from(printWindow.document.images).map((img) => waitForImageLoad(img)));
  if (printWindow.document.fonts?.ready) {
    await Promise.race([
      printWindow.document.fonts.ready,
      new Promise((resolve) => window.setTimeout(resolve, 2000)),
    ]);
  }
};

const PageActions: React.FC<PageActionsProps> = ({ page, pageIndex, scale, pages, coverName, onRename, onReset, onSwapPages, onDuplicatePageContent, onRemoveBackground, onAddGuide, onRestoreVersion }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState(page.noteToGraphiste ?? '');
  const [sending, setSending] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameText, setRenameText] = useState(page.name ?? '');
  const [renameSending, setRenameSending] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState<number | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<number | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isCover = !!page.coverType;
  const isTrombi = page.itemType === 'Trombi';
  const canSwap = !isCover && !isTrombi && !!onSwapPages && !!pages;
  const canDuplicate = !isCover && !isTrombi && !!onDuplicatePageContent && !!pages;

  // Swappable pages: interior, non-trombi, not self
  const swappablePages = useMemo(() => {
    if (!pages) return [];
    return pages
      .map((p, i) => ({ page: p, index: i }))
      .filter((item) => !item.page.coverType && item.page.itemType !== 'Trombi' && item.index !== pageIndex);
  }, [pages, pageIndex]);

  // Duplicate targets: interior (non-cover), not self — trombi shown but disabled
  const duplicateTargetPages = useMemo(() => {
    if (!pages) return [];
    return pages
      .map((p, i) => ({ page: p, index: i }))
      .filter((item) => !item.page.coverType && item.index !== pageIndex);
  }, [pages, pageIndex]);

  const handleDuplicate = () => {
    if (duplicateTarget !== null) {
      onDuplicatePageContent?.(pageIndex, duplicateTarget);
      setDuplicateOpen(false);
      setDuplicateTarget(null);
    }
  };

  const pageLabel = page.coverType === 'front'
    ? '1ère de couv.' + (coverName ? ` (${coverName})` : '')
    : page.coverType === 'back'
    ? '4ème de couv.'
    : page.coverType === 'tranche'
    ? 'Tranche'
    : `Page ${page.pageNumber}`;

  const handleSendNote = async () => {
    setSending(true);
    try {
      await fetch(editorUrl('/save_note_to_graphiste'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary_bloc_id: page.id, note: noteText }),
      });
      page.noteToGraphiste = noteText;
      setNoteOpen(false);
    } catch (err) {
      console.error('Failed to send note:', err);
    } finally {
      setSending(false);
    }
  };

  const handleRename = async () => {
    setRenameSending(true);
    try {
      await fetch(editorUrl('/save_page_name'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary_bloc_id: page.id, name: renameText.trim() }),
      });
      onRename?.(page.id, renameText.trim());
      setRenameOpen(false);
    } catch (err) {
      console.error('Failed to rename page:', err);
    } finally {
      setRenameSending(false);
    }
  };

  const handleSwap = () => {
    if (swapTarget !== null) {
      onSwapPages?.(pageIndex, swapTarget);
      setSwapOpen(false);
      setSwapTarget(null);
    }
  };

  const handlePrint = async () => {
    const el = document.querySelector(`[data-page-id="${page.id}"]`) as HTMLElement | null;
    if (!el) return;
    const pageW = page.pageWidth ?? DEFAULT_PAGE_WIDTH;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        console.warn('Popup blocked – cannot open print window');
        return;
      }

      const printablePage = clonePageForPrint(el, pageW);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - ${pageLabel}</title>
            <base href="${escapeHtml(document.baseURI)}">
            ${collectPrintableStyles()}
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              html, body { width: 100%; min-height: 100%; background: white; }
              body { display: flex; justify-content: center; align-items: flex-start; padding: 16px; }
              [data-print-page="true"] { flex: 0 0 auto; overflow: hidden; }
              @media print {
                @page { margin: 0; size: auto; }
                html, body { width: ${pageW}px; height: ${PAGE_HEIGHT}px; }
                body { display: block; padding: 0; }
                [data-print-page="true"] { width: ${pageW}px !important; height: ${PAGE_HEIGHT}px !important; box-shadow: none !important; border-radius: 0 !important; }
              }
            </style>
          </head>
          <body></body>
        </html>
      `);
      printWindow.document.body.appendChild(printablePage);
      printWindow.document.close();

      await waitForPrintAssets(printWindow);

      printWindow.focus();
      printWindow.print();
    } catch (err) {
      console.error('Failed to print page:', err);
    }
  };

  const isTranche = page.coverType === 'tranche';

  return (
    <>
      {isTranche ? (
        <div className="flex flex-col items-center gap-0.5 mt-1 leading-none">
          <span className="text-xs text-muted-foreground font-medium">
            {pageLabel}
          </span>
          <button
            onClick={() => setNoteOpen(true)}
            className={`p-0.5 rounded hover:bg-editor-sidebar-hover transition-colors ${noteText.trim() ? 'text-primary' : 'text-muted-foreground hover:text-editor-sidebar-active'}`}
          >
            <MessageCircle size={14} fill={noteText.trim() ? 'currentColor' : 'none'} />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3 mt-2">
          {/* Action dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-editor-sidebar-hover text-muted-foreground hover:text-editor-sidebar-active transition-colors">
                <MoreHorizontal size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="bg-popover z-50">
              <DropdownMenuItem className="gap-2 text-xs" onClick={handlePrint}>
                <Printer size={13} />
                Imprimer la page
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-xs" onClick={() => { if (window.confirm('Voulez-vous vraiment réinitialiser cette page ? Tous les éléments seront supprimés.')) onReset?.(pageIndex); }}>
                <RotateCcw size={13} />
                Réinitialiser la page
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-xs" onClick={() => { setRenameText(page.name ?? ''); setRenameOpen(true); }}>
                <Pencil size={13} />
                Renommer la page
              </DropdownMenuItem>
              {onAddGuide && (
                <DropdownMenuItem className="gap-2 text-xs" onClick={() => setGuideOpen(true)}>
                  <Ruler size={13} />
                  Ajouter un repère
                </DropdownMenuItem>
              )}
              {onRestoreVersion && (
                <DropdownMenuItem className="gap-2 text-xs" onClick={() => setHistoryOpen(true)}>
                  <History size={13} />
                  Historique de la page
                </DropdownMenuItem>
              )}
              {canSwap && (
                <DropdownMenuItem className="gap-2 text-xs" onClick={() => { setSwapTarget(null); setSwapOpen(true); }}>
                  <ArrowLeftRight size={13} />
                  Intervertir avec une autre page…
                </DropdownMenuItem>
              )}
              {canDuplicate && (
                <DropdownMenuItem className="gap-2 text-xs" onClick={() => { setDuplicateTarget(null); setDuplicateOpen(true); }}>
                  <Copy size={13} />
                  Dupliquer la page vers…
                </DropdownMenuItem>
              )}
              {page.background && (page.background.type !== 'color' || page.background.value !== '#FFFFFF') && (
                <DropdownMenuItem className="gap-2 text-xs" onClick={() => onRemoveBackground?.(pageIndex)}>
                  <ImageOff size={13} />
                  Enlever le fond de page
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Page number */}
          <span className="text-xs text-muted-foreground font-medium">
            {pageLabel}
          </span>

          {/* Note to graphiste */}
          <button
            onClick={() => setNoteOpen(true)}
            className={`p-1 rounded hover:bg-editor-sidebar-hover transition-colors ${noteText.trim() ? 'text-primary' : 'text-muted-foreground hover:text-editor-sidebar-active'}`}
          >
            <MessageCircle size={14} fill={noteText.trim() ? 'currentColor' : 'none'} />
          </button>
        </div>
      )}

      {/* Note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remarques pour les graphistes</DialogTitle>
            <DialogDescription>
              Votre remarque sera envoyée aux graphistes lors de la vérification de votre Yearbook
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Écrivez votre remarque ici..."
            className="min-h-[120px]"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setNoteOpen(false)}>
              Fermer
            </Button>
            <Button onClick={handleSendNote} disabled={sending || !noteText.trim()}>
              {sending ? 'Envoi…' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Renommer la page</DialogTitle>
            <DialogDescription>
              Donnez un nom à cette page pour mieux l'identifier
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameText}
            onChange={(e) => setRenameText(e.target.value)}
            placeholder="Nom de la page"
            onKeyDown={(e) => { if (e.key === 'Enter' && renameText.trim()) handleRename(); }}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleRename} disabled={renameSending}>
              {renameSending ? 'Envoi…' : 'Valider'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap dialog */}
      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Intervertir les pages</DialogTitle>
            <DialogDescription>
              Choisissez la page à intervertir avec <strong>{pageLabel}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-52 overflow-y-auto space-y-1">
            {swappablePages.map((item) => (
              <button
                key={item.page.id}
                onClick={() => setSwapTarget(item.index)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  swapTarget === item.index
                    ? 'bg-primary/15 text-primary font-medium ring-1 ring-primary/30'
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                Page {item.page.pageNumber}
                {item.page.name ? ` — ${item.page.name}` : ''}
              </button>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setSwapOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSwap} disabled={swapTarget === null}>
              Intervertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate page dialog */}
      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Dupliquer la page</DialogTitle>
            <DialogDescription>
              Choisissez la page de destination où dupliquer <strong>{pageLabel}</strong>. Le contenu de la page de destination sera remplacé.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-52 overflow-y-auto space-y-1">
            {duplicateTargetPages.map((item) => {
              const isTrombiItem = item.page.itemType === 'Trombi';
              return (
                <button
                  key={item.page.id}
                  onClick={() => !isTrombiItem && setDuplicateTarget(item.index)}
                  disabled={isTrombiItem}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    isTrombiItem
                      ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                      : duplicateTarget === item.index
                      ? 'bg-primary/15 text-primary font-medium ring-1 ring-primary/30'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  Page {item.page.pageNumber}
                  {isTrombiItem ? ' — Trombi' : item.page.name ? ` — ${item.page.name}` : ''}
                </button>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDuplicateOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleDuplicate} disabled={duplicateTarget === null}>
              Dupliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add guide dialog */}
      {onAddGuide && (
        <AddGuideDialog
          open={guideOpen}
          onOpenChange={setGuideOpen}
          pageWidth={page.pageWidth}
          existingGuides={page.guides ?? []}
          onAdd={(g) => onAddGuide(pageIndex, g)}
        />
      )}

      {/* Page history dialog */}
      {onRestoreVersion && (
        <PageHistoryDialog
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          pageId={page.id}
          pageLabel={pageLabel}
          onRestore={(elements, background) => onRestoreVersion(pageIndex, elements, background)}
        />
      )}
    </>
  );
};

export default PageActions;
